import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPackage,
  FiTrendingUp,
  FiTrendingDown,
  FiEdit,
  FiFilter,
  FiCheck,
  FiPlus,
  FiSearch,
  FiAlertTriangle,
  FiGrid,
  FiList,
  FiColumns,
  FiDownload,
  FiSave,
  FiX,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import { inventarioService, productosService, reportesService } from '../../services';
import { LoadingGrid } from '../../components/UI/Loading';
import MovimientoStockModal from '../../components/Inventario/MovimientoStockModal';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';

const Inventario = () => {
  const navigate = useNavigate();

  // Estados
  const [activeTab, setActiveTab] = useState('stock'); // Cambiado a 'stock' como principal
  const [inventario, setInventario] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // Vista por defecto: tabla

  // Filtros para inventario
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);
  const [department, setDepartment] = useState(''); // '' = todos
  const [page, setPage] = useState(1); // paginación (en cliente)

  // Departamentos (igual que en Productos); se envían como `department` al backend
  const departamentos = [
    { id: 'BETEL', label: 'Betel' },
    { id: 'NATURALANDLAB', label: 'Naturaland' },
    { id: 'INGREDIENT', label: 'Ingredientes' },
    { id: 'PACKAGING_MATERIAL', label: 'Empaques' },
    { id: 'OTHER_BRANDS', label: 'Otras Marcas' }
  ];

  // Modal de movimiento
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Estados para edición inline
  const [editingProductId, setEditingProductId] = useState(null);
  const [editedMinStock, setEditedMinStock] = useState('');
  const [editedMaxStock, setEditedMaxStock] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Estados para descarga de reportes
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);

  // Wave SVG dinámica
  const menuRef    = useRef(null);
  const sec1Ref    = useRef(null);
  const sec2Ref    = useRef(null);
  const sec3Ref    = useRef(null);
  const sec4Ref    = useRef(null);
  const svgPathRef = useRef(null);

  const buildWavePath = useCallback(() => {
    if (!menuRef.current || !sec1Ref.current || !sec2Ref.current || !sec3Ref.current || !sec4Ref.current || !svgPathRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const W = menuRect.width;
    if (W === 0) return;
    const VB = 1000, TOP = 3, BOT = 53, half = 18, scale = VB / W;
    const b1 = (sec1Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b2 = (sec2Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b3 = (sec3Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const d = [
      `M 0,${TOP}`,
      `L ${b1 - half},${TOP}`,
      `C ${b1},${TOP} ${b1},${BOT} ${b1 + half},${BOT}`,
      `L ${b2 - half},${BOT}`,
      `C ${b2},${BOT} ${b2},${TOP} ${b2 + half},${TOP}`,
      `L ${b3 - half},${TOP}`,
      `C ${b3},${TOP} ${b3},${BOT} ${b3 + half},${BOT}`,
      `L ${VB},${BOT}`,
    ].join(' ');
    svgPathRef.current.setAttribute('d', d);
  }, []);

  useEffect(() => {
    if (!menuRef.current) return;
    buildWavePath();
    const t = setTimeout(buildWavePath, 100);
    const ro = new ResizeObserver(buildWavePath);
    [menuRef, sec1Ref, sec2Ref, sec3Ref, sec4Ref].forEach(r => { if (r.current) ro.observe(r.current); });
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [buildWavePath, isLoading]);

  useEffect(() => {
    const handler = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target))
        setShowFilterDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab, includeInactive, lowStockOnly, outOfStockOnly, department]);

  // Volver a la página 1 cuando cambian la búsqueda o los filtros
  useEffect(() => {
    setPage(1);
  }, [searchTerm, includeInactive, lowStockOnly, outOfStockOnly, department, activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (activeTab === 'stock') {
        // Cargar inventario actual con filtros
        const result = await inventarioService.getInventory({
          include_inactive: includeInactive,
          low_stock_only: lowStockOnly,
          out_of_stock_only: outOfStockOnly,
          ...(department ? { department } : {}),
          limit: 100
        });
        if (result.success) {
          // Mapear los campos de la API a los campos que el componente espera
          const inventoryData = result.data?.inventory || [];
          const mappedInventory = inventoryData.map(item => ({
            ...item,
            code: item.product_code,
            sku: item.product_code,
            stock: item.current_stock,
            stock_min: item.min_stock,
            stock_max: item.max_stock,
            unit_cost: item.cost_price,
            is_active: item.status === 'ACTIVE'
          }));
          setInventario(mappedInventory);
        }
      } else if (activeTab === 'movimientos') {
        const result = await inventarioService.getMovements({ limit: 50 });
        if (result.success) {
          // formatApiResponse ya desempaqueta: result.data ES el array de movimientos.
          const movs = Array.isArray(result.data)
            ? result.data
            : (result.data?.data || result.data?.movements || []);
          setMovimientos(movs);
        }
      }

      // Cargar estadísticas
      const statsResult = await inventarioService.getStats();
      if (statsResult.success) {
        setStats(statsResult.data?.data || statsResult.data);
      }
    } catch (err) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAjustarStock = (productId = null) => {
    if (productId) {
      navigate(`/system/inventario/ajustar?product_id=${productId}`);
    } else {
      navigate('/system/inventario/ajustar');
    }
  };

  const handleOpenModal = (product, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleMovimientoSuccess = (updatedProduct) => {
    // Recargar datos después de un movimiento exitoso
    loadData();
  };

  const getStockStatus = (product) => {
    if (product.stock === 0) {
      return { label: 'AGOTADO', className: 'danger' };
    } else if (product.stock <= product.stock_min) {
      return { label: 'BAJO', className: 'warning' };
    } else {
      return { label: 'OK', className: 'success' };
    }
  };

  const getOperationIcon = (movementType) => {
    const t = (movementType || '').toLowerCase();
    if (['in', 'add', 'entrada'].includes(t)) {
      return <FiTrendingUp className="text-success" />;
    }
    return <FiTrendingDown className="text-danger" />;
  };

  const getOperationLabel = (movementType) => {
    const labels = {
      in: 'Entrada', out: 'Salida', adjustment: 'Ajuste', transfer: 'Transferencia',
      add: 'Entrada', subtract: 'Salida', set: 'Ajuste', entrada: 'Entrada', salida: 'Salida'
    };
    return labels[(movementType || '').toLowerCase()] || movementType;
  };

  const getOperationClass = (movementType) => {
    const t = (movementType || '').toLowerCase();
    if (['in', 'add', 'entrada'].includes(t)) return 'success';
    if (['out', 'subtract', 'salida'].includes(t)) return 'danger';
    return 'info';
  };

  const handleEditStock = (item, e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingProductId(item.product_id);
    setEditedMinStock(item.stock_min || 0);
    setEditedMaxStock(item.stock_max || 0);
  };

  const handleCancelEdit = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingProductId(null);
    setEditedMinStock('');
    setEditedMaxStock('');
  };

  const handleSaveStock = async (productId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsSaving(true);
    setError(null);

    try {
      const updateData = {
        min_stock: parseFloat(editedMinStock) || 0,
        max_stock: parseFloat(editedMaxStock) || 0
      };

      const result = await productosService.updateProducto(productId, updateData);

      if (result.success) {
        // Actualizar el inventario local
        setInventario(prevInventario =>
          prevInventario.map(item =>
            item.product_id === productId
              ? { ...item, stock_min: updateData.min_stock, stock_max: updateData.max_stock }
              : item
          )
        );
        setEditingProductId(null);
        setEditedMinStock('');
        setEditedMaxStock('');
      } else {
        setError(result.message || 'Error al actualizar stock');
      }
    } catch (error) {
      console.error('Error al guardar stock:', error);
      setError('Error al guardar los cambios');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);

    try {
      // Llamar al servicio de reportes
      const params = {
        format: format, // csv o pdf
        date_from: startDate,
        date_to: endDate,
        include_inactive: includeInactive,
        low_stock_only: lowStockOnly,
        out_of_stock_only: outOfStockOnly
      };

      const result = await reportesService.exportarInventario(params);

      if (result.success) {
        // Determinar el tipo MIME según el formato
        const mimeTypes = {
          csv: 'text/csv',
          pdf: 'application/pdf',
          excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        // Determinar la extensión del archivo
        const extensions = {
          csv: 'csv',
          pdf: 'pdf',
          excel: 'xlsx'
        };

        // Crear blob y descargar
        const blob = new Blob([result.data], {
          type: mimeTypes[format] || 'application/octet-stream'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generar nombre de archivo apropiado
        const fileName = startDate && endDate
          ? `inventario_${startDate}_${endDate}.${extensions[format]}`
          : `inventario_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setShowDownloadModal(false);
      } else {
        setError(result.message || 'Error al generar el reporte');
      }
    } catch (err) {
      console.error('Error al descargar reporte:', err);
      setError('Error al descargar el reporte');
    } finally {
      setIsDownloading(false);
    }
  };

  const filteredInventario = inventario.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.product_name?.toLowerCase().includes(search) ||
      item.code?.toLowerCase().includes(search) ||
      item.sku?.toLowerCase().includes(search) ||
      item.product_code?.toLowerCase().includes(search) ||
      item.barcode?.toLowerCase().includes(search) // UPC del producto
    );
  });

  // Paginación en cliente: el backend devuelve TODO el inventario ya filtrado
  // por departamento/stock/estado, y la búsqueda se aplica encima. Así el
  // paginador respeta a la vez los filtros y el buscador.
  const INV_PER_PAGE = 20;
  const invTotalPages = Math.max(1, Math.ceil(filteredInventario.length / INV_PER_PAGE));
  const paginatedInventario = filteredInventario.slice((page - 1) * INV_PER_PAGE, page * INV_PER_PAGE);
  const changePage = (newPage) => {
    if (newPage < 1 || newPage > invTotalPages || newPage === page) return;
    setPage(newPage);
  };

  const filteredMovimientos = movimientos.filter(mov => {
    const matchesSearch = !searchTerm ||
      mov.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mov.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (isLoading && inventario.length === 0 && movimientos.length === 0) {
    return <LoadingGrid message="Cargando inventario..." />;
  }

  return (
    <div className="inventario-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiAlertTriangle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">INVENTARIO</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu" ref={menuRef}>
          <svg className="action-bar-wave" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 56" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="waveGradInventario" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stopColor="#a64b95" stopOpacity="0.75" />
                <stop offset="45%"  stopColor="#c975b9" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#7e2371" stopOpacity="0.75" />
              </linearGradient>
            </defs>
            <path ref={svgPathRef} d="" fill="none" stroke="url(#waveGradInventario)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>

          <button className="menu-item menu-item--first" ref={sec1Ref} onClick={() => handleAjustarStock()}>
            <span className="add-btn-plus">+</span>
            <span className="add-btn-label">AJUSTAR STOCK</span>
          </button>

          <div className="menu-item menu-item--search" ref={sec2Ref}>
            <input
              type="text"
              className="search-input"
              placeholder={activeTab === 'stock' ? "Buscar producto, código o UPC..." : "Buscar producto o razón..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="menu-item menu-item--views" ref={sec3Ref}>
            <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Vista de tarjetas"><FiGrid /></button>
            <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Vista de lista"><FiList /></button>
            <button className={`view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')} title="Vista de tabla"><FiColumns /></button>
          </div>

          <div className="menu-item menu-item--checkbox" ref={sec4Ref}>
            <div className="filter-dropdown-wrapper" ref={filterDropdownRef}>
              <button
                className={`view-btn ${(includeInactive || lowStockOnly || outOfStockOnly || department) ? 'active' : ''}`}
                onClick={() => setShowFilterDropdown(prev => !prev)}
                title="Filtrar inventario"
              >
                <FiFilter />
              </button>
              {showFilterDropdown && (
                <div className="filter-popover">
                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px' }}>STOCK</div>
                  <button
                    className={`filter-popover-item ${lowStockOnly ? 'active' : ''}`}
                    onClick={() => { setLowStockOnly(v => !v); if (!lowStockOnly) setOutOfStockOnly(false); }}
                  >
                    {lowStockOnly && <FiCheck />} Solo stock bajo
                  </button>
                  <button
                    className={`filter-popover-item ${outOfStockOnly ? 'active' : ''}`}
                    onClick={() => { setOutOfStockOnly(v => !v); if (!outOfStockOnly) setLowStockOnly(false); }}
                  >
                    {outOfStockOnly && <FiCheck />} Solo agotados
                  </button>

                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', borderTop: '1px solid #f1f5f9', marginTop: '4px' }}>ESTADO</div>
                  <button
                    className={`filter-popover-item ${includeInactive ? 'active' : ''}`}
                    onClick={() => setIncludeInactive(v => !v)}
                  >
                    {includeInactive && <FiCheck />} Incluir inactivos
                  </button>

                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', borderTop: '1px solid #f1f5f9', marginTop: '4px' }}>DEPARTAMENTO</div>
                  {departamentos.map(d => (
                    <button
                      key={d.id}
                      className={`filter-popover-item ${department === d.id ? 'active' : ''}`}
                      onClick={() => setDepartment(prev => prev === d.id ? '' : d.id)}
                    >
                      {department === d.id && <FiCheck />} {d.label}
                    </button>
                  ))}

                  {(includeInactive || lowStockOnly || outOfStockOnly || department) && (
                    <button
                      className="filter-popover-item"
                      style={{ borderTop: '1px solid #f1f5f9', marginTop: '4px', color: '#7B2D8E', fontWeight: 700 }}
                      onClick={() => { setIncludeInactive(false); setLowStockOnly(false); setOutOfStockOnly(false); setDepartment(''); setShowFilterDropdown(false); }}
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              )}
            </div>
            <button className="view-btn" onClick={handleDownload} title="Descargar reporte">
              <FiDownload />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs integradas */}
      <div className="inventory-tabs">
        <button
          className={`inventory-tab ${activeTab === 'stock' ? 'active' : ''}`}
          onClick={() => setActiveTab('stock')}
        >
          <FiPackage /> STOCK ACTUAL
        </button>
        <button
          className={`inventory-tab ${activeTab === 'movimientos' ? 'active' : ''}`}
          onClick={() => setActiveTab('movimientos')}
        >
          <FiTrendingUp /> MOVIMIENTOS
        </button>
      </div>


      {/* Contenido según tab activo */}
      {activeTab === 'stock' && (
        <>
          {filteredInventario.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FiPackage size={48} />
              </div>
              <h3 className="empty-state-title">
                {searchTerm || lowStockOnly || outOfStockOnly || department
                  ? 'No se encontraron productos'
                  : 'No hay productos en el inventario'}
              </h3>
              <p className="empty-state-description">
                {searchTerm || lowStockOnly || outOfStockOnly || department
                  ? 'No se encontraron productos con los filtros aplicados'
                  : 'Comienza agregando productos al inventario'}
              </p>
            </div>
          ) : viewMode === 'table' ? (
            <div className="table-container">
              <table className="data-table data-table--inventario">
                <thead>
                  <tr>
                    <th>PRODUCTO</th>
                    <th className="text-center">STOCK ACTUAL</th>
                    <th className="text-center">STOCK MÍNIMO</th>
                    <th className="text-center">STOCK MÁXIMO</th>
                    <th className="text-right">COSTO UNIT.</th>
                    <th className="text-right">VALOR TOTAL</th>
                    <th className="text-center">ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInventario.map((item) => {
                    const status = getStockStatus(item);
                    const isEditing = editingProductId === item.product_id;
                    return (
                      <tr key={item.product_id}>
                        <td>
                          <strong>{item.product_name}</strong>
                          {!item.is_active && (
                            <span className="badge badge--secondary ml-2">Inactivo</span>
                          )}
                        </td>
                        <td className="text-center">
                          <strong>{item.stock || 0}</strong>
                        </td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editedMinStock}
                              onChange={(e) => setEditedMinStock(e.target.value)}
                              min="0"
                              step="0.01"
                              style={{
                                width: '80px',
                                padding: '4px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '13px',
                                textAlign: 'center'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span>{item.stock_min || 0}</span>
                          )}
                        </td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editedMaxStock}
                              onChange={(e) => setEditedMaxStock(e.target.value)}
                              min="0"
                              step="0.01"
                              style={{
                                width: '80px',
                                padding: '4px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '13px',
                                textAlign: 'center'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span>{item.stock_max || 0}</span>
                          )}
                        </td>
                        <td className="text-right">
                          ${(item.unit_cost || 0).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right">
                          <strong>
                            ${((item.stock || 0) * (item.unit_cost || 0)).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                          </strong>
                        </td>
                        <td className="text-center">
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                className="btn btn--sm btn--success"
                                onClick={(e) => handleSaveStock(item.product_id, e)}
                                disabled={isSaving}
                                title="Guardar cambios"
                              >
                                <FiSave />
                              </button>
                              <button
                                className="btn btn--sm btn--ghost"
                                onClick={handleCancelEdit}
                                disabled={isSaving}
                                title="Cancelar"
                              >
                                <FiX />
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                className="btn btn--sm btn--secondary"
                                onClick={(e) => handleEditStock(item, e)}
                                title="Editar stock mín/máx"
                              >
                                <FiEdit />
                              </button>
                              <button
                                className="btn btn--sm btn--primary"
                                onClick={(e) => handleOpenModal(item, e)}
                                title="Realizar movimiento"
                              >
                                <FiPackage />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
              {filteredInventario.map((item) => {
                const status = getStockStatus(item);
                return (
                  <div
                    key={item.product_id}
                    className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
                    onClick={() => handleAjustarStock(item.product_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {viewMode === 'grid' ? (
                      <>
                        <div className={`content-thumbnail ${getThumbnailType(status.className)}`}>
                          <div className="inventory-label">
                            {item.product_name}
                          </div>
                          <div className="inventory-stock">
                            {item.stock || 0}
                          </div>
                        </div>
                        <div className="content-info">
                          <div className="content-title">{item.code || item.sku || 'Sin código'}</div>
                          <div className="content-subtitle">Stock mín: {item.stock_min || 0}</div>
                          <div className="content-meta">
                            <span className={`status-badge status-badge--${status.className}`}>
                              {status.label}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`content-thumbnail-small ${getThumbnailType(status.className)}`}>
                          <FiPackage className="thumbnail-icon" />
                        </div>
                        <div className="content-info-list">
                          <div className="content-title">{item.product_name}</div>
                          <div className="content-subtitle">{item.code || item.sku || 'Sin código'}</div>
                          <div className="content-meta">
                            <span className="meta-item">
                              Stock: <strong>{item.stock || 0}</strong>
                            </span>
                            <span className="meta-item">
                              Mín: {item.stock_min || 0}
                            </span>
                            <span className={`status-badge status-badge--${status.className}`}>
                              {status.label}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {filteredInventario.length > 0 && invTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', margin: '20px 0 8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => changePage(page - 1)}
                disabled={page <= 1}
                title="Página anterior"
                aria-label="Página anterior"
                style={{
                  width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #7B2D8E',
                  background: '#fff', color: '#7B2D8E', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.4 : 1
                }}
              >
                <FiChevronLeft size={20} />
              </button>
              <span style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 600 }}>
                Página {page} de {invTotalPages} · {filteredInventario.length} producto(s)
              </span>
              <button
                type="button"
                onClick={() => changePage(page + 1)}
                disabled={page >= invTotalPages}
                title="Página siguiente"
                aria-label="Página siguiente"
                style={{
                  width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #7B2D8E',
                  background: '#fff', color: '#7B2D8E', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', cursor: page >= invTotalPages ? 'not-allowed' : 'pointer', opacity: page >= invTotalPages ? 0.4 : 1
                }}
              >
                <FiChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal de Movimiento de Stock */}
      <MovimientoStockModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        producto={selectedProduct}
        onSuccess={handleMovimientoSuccess}
      />

      {activeTab === 'movimientos' && (
        <div className="table-container">
          <table className="data-table data-table--movimientos">
            <thead>
              <tr>
                <th>FECHA</th>
                <th>PRODUCTO</th>
                <th>TIPO</th>
                <th className="text-right">CANTIDAD</th>
                <th>RAZÓN</th>
                <th>USUARIO</th>
                <th>NOTAS</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovimientos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No hay movimientos registrados
                  </td>
                </tr>
              ) : (
                filteredMovimientos.map((mov, index) => (
                  <tr key={mov.id || index}>
                    <td>{new Date(mov.created_at || mov.movement_date).toLocaleDateString()}</td>
                    <td><strong>{mov.product_name || `Producto #${mov.product_id}`}</strong></td>
                    <td>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: 600,
                          color: getOperationClass(mov.movement_type) === 'success'
                            ? '#16a34a'
                            : getOperationClass(mov.movement_type) === 'danger'
                              ? '#dc2626'
                              : '#2563eb'
                        }}
                      >
                        {getOperationIcon(mov.movement_type)}
                        {getOperationLabel(mov.movement_type) || mov.movement_type || '-'}
                      </span>
                    </td>
                    <td className="text-right"><strong>{mov.quantity_change || mov.quantity}</strong></td>
                    <td>{mov.reason || '-'}</td>
                    <td>{mov.user_name || mov.created_by || '-'}</td>
                    <td>{mov.notes || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de descarga de reportes */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Inventario"
        moduleName="inventario"
        isLoading={isDownloading}
      />
    </div>
  );

  // Funciones helper
  function getThumbnailType(statusClass) {
    switch (statusClass) {
      case 'success':
        return 'blue-dark-gradient'; // Stock OK = Morado
      case 'warning':
        return 'blue-dark-gradient'; // Stock Bajo = Morado
      case 'danger':
        return 'blue-dark-gradient'; // Stock Agotado = Morado
      default:
        return 'blue-dark-gradient';
    }
  }
};

export default Inventario;
